const isCodeChar = (char: string) => /[\p{L}\d]/u.test(char);
const countCodeChars = (value: string) => [...value].filter(isCodeChar).length;

/** Rewrites the input's value through `mask`, keeping the caret after the
 *  same letter/digit it followed before (masks add or drop separators). */
export function applyMask(
  input: HTMLInputElement,
  mask: (value: string) => string
) {
  const caret = input.selectionStart ?? input.value.length;
  // Masking the text before the caret drops the same characters the full mask
  // does, so what remains is what the caret should follow.
  const charsBeforeCaret = countCodeChars(mask(input.value.slice(0, caret)));
  const masked = mask(input.value);
  if (masked === input.value) return;
  input.value = masked;
  let position = 0;
  for (let seen = 0; position < masked.length && seen < charsBeforeCaret;) {
    if (isCodeChar(masked[position] ?? '')) seen += 1;
    position += 1;
  }
  input.setSelectionRange(position, position);
}

type Slot = { accepts: (char: string) => boolean; upper: boolean };

type MaskToken =
  | ({ kind: 'slot'; min: number; max: number } & Slot)
  | { kind: 'literal'; char: string };

const letter: Slot = { accepts: (char) => /\p{L}/u.test(char), upper: true };
const slotChars = new Map<string, Slot>([
  ['0', { accepts: (char) => /\d/.test(char), upper: false }],
  ['a', letter],
  ['A', letter],
  ['*', { accepts: (char) => /[\p{L}\d]/u.test(char), upper: false }],
]);

/**
 * Parses a pattern mask: `0` is a digit, `a`/`A` a letter, `*` a letter or
 * digit, and `{n}` / `{min,max}` after one of them repeats it. Any other
 * character is a literal separator, e.g. `0000 aa` or `0{1,5}`.
 */
export function parsePatternMask(mask: string): MaskToken[] {
  const tokens: MaskToken[] = [];
  for (let i = 0; i < mask.length; i += 1) {
    const char = mask[i] ?? '';
    const slot = slotChars.get(char);
    if (!slot) {
      tokens.push({ kind: 'literal', char });
      continue;
    }
    const quantifier = /^\{(\d+)(?:,(\d+))?\}/.exec(mask.slice(i + 1));
    const min = quantifier ? Number(quantifier[1]) : 1;
    const max = quantifier ? Number(quantifier[2] ?? quantifier[1]) : 1;
    if (quantifier) i += quantifier[0].length;
    tokens.push({ kind: 'slot', ...slot, min, max });
  }
  return tokens;
}

/** Upper-cases `char` unless that turns it into several characters (ß → SS). */
const toUpper = (char: string) => {
  const upper = char.toUpperCase();
  return [...upper].length === 1 ? upper : char;
};

/**
 * Fills the mask's slots from `value`, left to right, and reports how many
 * characters each token took.
 */
function fillPatternMask(value: string, tokens: MaskToken[]) {
  const chars = [...value].filter((char) =>
    tokens.some((token) => token.kind === 'slot' && token.accepts(char))
  );
  const taken = tokens.map(() => 0);
  let out = '';
  let pendingLiterals = '';
  let index = 0;
  tokens.forEach((token, t) => {
    if (index >= chars.length) return;
    if (token.kind === 'literal') {
      pendingLiterals += token.char;
      return;
    }
    while ((taken[t] ?? 0) < token.max && index < chars.length) {
      const char = chars[index] ?? '';
      if (!token.accepts(char)) {
        // Past its minimum an optional slot hands the character on to the
        // next one; before it, the character can't go anywhere and is dropped.
        if ((taken[t] ?? 0) >= token.min) break;
        index += 1;
        continue;
      }
      index += 1;
      out += pendingLiterals + (token.upper ? toUpper(char) : char);
      pendingLiterals = '';
      taken[t] = (taken[t] ?? 0) + 1;
    }
  });
  return { out, taken };
}

/**
 * Formats `value` into the mask: characters a slot doesn't accept are dropped
 * and separators are inserted as the buyer types past them.
 */
export function formatPatternMask(value: string, mask: string): string {
  return fillPatternMask(value, parsePatternMask(mask)).out;
}

/** Whether `value` is already formatted and fills every slot to its minimum. */
export function isPatternMaskComplete(value: string, mask: string): boolean {
  const tokens = parsePatternMask(mask);
  const { out, taken } = fillPatternMask(value, tokens);
  return (
    out === value &&
    tokens.every(
      (token, t) => token.kind === 'literal' || (taken[t] ?? 0) >= token.min
    )
  );
}
